import { useEffect, useRef } from 'react';

// Global reference count to track how many components are requesting a scroll lock
let lockCount = 0;

/**
 * Hook to lock body scrolling.
 * Uses a reference counting mechanism to handle multiple modals opening/closing.
 * 
 * @param isLocked - Boolean to determine if the scroll should be locked
 */
export const useScrollLock = (isLocked: boolean) => {
    // Track if this specific hook instance has actively incremented the lock count
    const isLockedRef = useRef(false);

    useEffect(() => {
        const handleLock = () => {
            if (isLocked) {
                if (!isLockedRef.current) {
                    lockCount++;
                    isLockedRef.current = true;
                }
            } else {
                if (isLockedRef.current) {
                    lockCount--;
                    isLockedRef.current = false;
                }
            }

            updateScrollState();
        };

        handleLock();

        return () => {
            // Cleanup: if this instance held a lock, release it on unmount/change
            if (isLockedRef.current) {
                lockCount--;
                isLockedRef.current = false;
                updateScrollState();
            }
        };
    }, [isLocked]);

    const updateScrollState = () => {
        if (lockCount > 0) {
            document.body.style.overflow = 'hidden';
            // Ensure specific consistent behavior
            document.body.style.position = '';
        } else {
            // Reset to default
            document.body.style.overflow = '';

            // Cleanup potential leftover styles from legacy code (like PreSessionModal)
            if (document.documentElement.style.overflow === 'hidden') {
                document.documentElement.style.overflow = '';
            }
        }
    };
};

export default useScrollLock;
